<script setup>
import { computed } from 'vue';
import { formatTime } from '@chatwoot/utils';
import ReportCard from '../ReportCard.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  comparisonLabel: {
    type: String,
    default: '',
  },
  previousLabel: {
    type: String,
    default: '',
  },
  withinTargetLabel: {
    type: String,
    default: '',
  },
  attentionLabel: {
    type: String,
    default: '',
  },
  variationLabel: {
    type: String,
    default: '',
  },
  loadingMessage: {
    type: String,
    default: '',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  metrics: {
    type: Array,
    default: () => [],
  },
});

const normalizedMetrics = computed(() =>
  props.metrics.map(metric => {
    const currentValue = Number(metric.value || 0);
    const previousValue = Number(metric.previous || 0);
    const maxValue = Math.max(currentValue, previousValue, 1);
    const percentage = Math.min((currentValue / maxValue) * 100, 100);
    const isWithinTarget = !previousValue || currentValue <= previousValue;
    const delta = previousValue
      ? Math.round(((currentValue - previousValue) / previousValue) * 100)
      : 0;

    return {
      ...metric,
      percentage,
      isWithinTarget,
      delta,
    };
  })
);

const gaugeStyle = metric => {
  return {
    strokeDasharray: `${metric.percentage} 100`,
  };
};

const deltaClass = metric => {
  return metric.delta <= 0
    ? 'text-green-700 dark:text-green-300'
    : 'text-red-600 dark:text-red-300';
};

const formattedDelta = delta => {
  if (!delta) return '0%';
  return `${delta > 0 ? '+' : ''}${delta}%`;
};
</script>

<template>
  <ReportCard compact :is-loading="isLoading" :loading-message="loadingMessage">
    <div class="flex items-center justify-between mb-6">
      <h3 class="mb-0 text-xl font-medium text-slate-900 dark:text-slate-50">
        {{ title }}
      </h3>
      <span class="text-sm text-slate-500 dark:text-slate-400">
        {{ comparisonLabel }}
      </span>
    </div>
    <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div
        v-for="metric in normalizedMetrics"
        :key="metric.key"
        class="flex flex-col items-center px-4 text-center md:border-l md:first:border-l-0 border-slate-100 dark:border-slate-700"
      >
        <span
          class="mb-3 text-sm font-medium text-slate-700 dark:text-slate-200"
        >
          {{ metric.label }}
        </span>
        <svg class="w-full max-w-[12rem]" viewBox="0 0 120 70">
          <path
            d="M 15 60 A 45 45 0 0 1 105 60"
            pathLength="100"
            class="fill-none stroke-slate-200 dark:stroke-slate-700"
            stroke-width="10"
            stroke-linecap="round"
          />
          <path
            d="M 15 60 A 45 45 0 0 1 105 60"
            pathLength="100"
            class="fill-none"
            :class="
              metric.isWithinTarget ? 'stroke-green-500' : 'stroke-red-500'
            "
            stroke-width="10"
            stroke-linecap="round"
            :style="gaugeStyle(metric)"
          />
        </svg>
        <strong
          class="-mt-3 text-xl leading-6 text-slate-900 dark:text-slate-50"
        >
          {{ formatTime(metric.value) }}
        </strong>
        <span class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ previousLabel }}: {{ formatTime(metric.previous) }}
        </span>
        <span class="mt-1 text-xs font-medium" :class="deltaClass(metric)">
          {{ formattedDelta(metric.delta) }} {{ variationLabel }}
        </span>
        <span
          class="px-3 py-1 mt-3 text-xs font-medium rounded-full"
          :class="
            metric.isWithinTarget
              ? 'text-green-700 bg-green-50 dark:text-green-200 dark:bg-green-900'
              : 'text-red-700 bg-red-50 dark:text-red-200 dark:bg-red-900'
          "
        >
          {{ metric.isWithinTarget ? withinTargetLabel : attentionLabel }}
        </span>
      </div>
    </div>
  </ReportCard>
</template>
